"use client"

import { ToastAction } from "@radix-ui/react-toast"
import { HomeIcon, LogOut, Settings, ShoppingBag, User, UserCircle } from "lucide-react"
import { useTranslations } from "next-intl"
import { usePostHog } from "posthog-js/react"
import { useCallback, useEffect, useState } from "react"

import { logout } from "@/app/[locale]/auth/actions"
import NonAuthHeader from "@/components/header/NonAuthHeader"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useMerchantOngoingOrders } from "@/hooks/merchant/useMerchantOngoingOrders"
import { useToast } from "@/hooks/use-toast"
import { useGetSupaUser } from "@/hooks/useGetSupaUser"
import { Link, usePathname, useRouter } from "@/i18n/routing"
import { ROUTES } from "@/routes"

import { LanguageSelector } from "../LanguageSelector"
import SpectraLogo from "../shared/SpectraLogo"
import { Badge } from "../ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog"
import { MerchantProfileUpdate } from "./MerchantProfileUpdate"
import { StripeSettings } from "./StripeSettings"
import { useIsMobile, useMerchantProfile } from "@/hooks/useGlobalStore"
import { CONST } from "@/constants"
import IconLogo from "../shared/IconLogo"

export default function MerchantHeader() {
  const posthog = usePostHog()
  const t = useTranslations("merchant.header")
  const { merchantProfile } = useMerchantProfile()
  const { ongoingOrders } = useMerchantOngoingOrders()
  const isMobile = useIsMobile()

  const isPendingOrder = ongoingOrders.length > 0

  const [isProfileUpdateOpen, setIsProfileUpdateOpen] = useState(false)
  const [isStripeConnectionOpen, setIsStripeConnectionOpen] = useState(false)
  const supaUser = useGetSupaUser()

  const router = useRouter()
  const path = usePathname()
  const isInMerchantDashboard = path.endsWith(ROUTES.merchant)
  const isInMerchantOngoingOrders = path.endsWith(ROUTES.merchantOngoingOrders)

  const { toast } = useToast()

  useEffect(() => {
    if (isPendingOrder && !isInMerchantOngoingOrders) {
      toast({
        title: t("notifications.new_order"),
        description: t("notifications.new_order_description"),
        duration: Infinity,
        action: (
          <ToastAction altText="View order" onClick={() => router.push(ROUTES.merchantOngoingOrders)} asChild>
            <Button className="animate-pulse">{t("notifications.view_order")}</Button>
          </ToastAction>
        ),
      })
    }
  }, [ongoingOrders, isPendingOrder, toast, router, isInMerchantOngoingOrders])

  if (!merchantProfile) {
    return <NonAuthHeader />
  }

  return (
    <div className="flex items-center justify-between">
      <Link href="/">
        <div className="flex items-baseline gap-2 font-glacialIndifference">
          {isMobile ? <IconLogo className="size-6 translate-y-[5px]" /> : <SpectraLogo />}
          <h1 className="text-muted-foreground">{t("title")}</h1>
        </div>
      </Link>
      <div className="flex items-center space-x-1">
        <MerchantProfileStatus
          merchantProfileIsActive={merchantProfile.activated}
          merchantProfileStripeOnboarded={merchantProfile.stripeOnboarded}
        />
        {supaUser && (
          <MerchantProfileUpdate
            merchantProfile={merchantProfile}
            isOpen={isProfileUpdateOpen}
            setIsOpen={setIsProfileUpdateOpen}
            showUpdatePassword
          />
        )}
        <StripeSettings
          isOpen={isStripeConnectionOpen}
          setIsOpen={setIsStripeConnectionOpen}
          merchantProfile={merchantProfile}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <User className="size-5" />
              <span className="sr-only">Merchant menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="[&>*]:cursor-pointer">
            {!isInMerchantDashboard && (
              <DropdownMenuItem onClick={() => router.push(ROUTES.merchant)}>
                <HomeIcon className="mr-2 size-4" />
                <span>{t("menu.dashboard")}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setIsProfileUpdateOpen(true)}>
              <UserCircle className="mr-2 size-4" />
              <span>{t("menu.profile")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => router.push(ROUTES.merchantOrders)}>
              <ShoppingBag className="mr-2 size-4" />
              <span>{t("menu.orders")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setIsStripeConnectionOpen(true)}>
              <Settings className="mr-2 size-4" />
              <span>{t("menu.stripe_settings")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className=" text-red-600"
              onClick={() => {
                posthog.reset()
                logout()
              }}
            >
              <LogOut className="mr-2 size-4" />
              <span>{t("menu.disconnect")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative rounded-full"
              onClick={() => router.push(ROUTES.merchantOngoingOrders)}
            >
              <ShoppingBag className="size-4" />
              <span className="sr-only">{t("orders_tooltip")}</span>
              {isPendingOrder && (
                <Badge
                  variant="success"
                  className="absolute -right-1 -top-1 flex size-5 animate-pulse items-center justify-center rounded-full p-0"
                >
                  {ongoingOrders.length}
                </Badge>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("orders_tooltip")}</TooltipContent>
        </Tooltip>
        <LanguageSelector />
      </div>
    </div>
  )
}

function MerchantProfileStatus({
  merchantProfileIsActive,
  merchantProfileStripeOnboarded,
}: {
  merchantProfileIsActive: boolean
  merchantProfileStripeOnboarded: boolean
}) {
  const t = useTranslations("merchant.header")

  const isActive = merchantProfileIsActive && merchantProfileStripeOnboarded

  const description = useCallback(() => {
    if (isActive) {
      return t("status.dialog.active_description")
    }

    return (
      <div className="space-y-2 ">
        {!merchantProfileIsActive && <p className="mb-6">{t("status.dialog.inactive_description")}</p>}
        {!merchantProfileStripeOnboarded && (
          <div className="mt-2 space-y-2 rounded-md bg-red-50 p-2">
            <h3 className="font-bold text-primary">{t("status.dialog.stripe_onboarding_required")}</h3>
            <div className="flex items-center gap-2">
              <p className="mb-2 text-primary">{t("status.dialog.stripe_onboarding_required_description")}</p>
              <Button onClick={() => window.open(CONST.STRIPE_DASHBOARD_URL, "_blank")} className="text-xs" size="sm">
                {t("status.dialog.view_stripe_documents")}
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }, [isActive, t, merchantProfileIsActive, merchantProfileStripeOnboarded])

  return (
    <Dialog>
      <DialogTrigger>
        <Badge variant="outline" className="flex items-center gap-2 bg-transparent">
          <div className={`size-2 rounded-full ${isActive ? "bg-green-500" : "bg-red-500"}`} />
          {isActive ? t("status.active") : t("status.inactive")}
        </Badge>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("status.dialog.title")}</DialogTitle>
        </DialogHeader>
        <DialogDescription>{description()}</DialogDescription>
      </DialogContent>
    </Dialog>
  )
}
